import React, { useState } from 'react';
import styled from 'styled-components';
import Colours from '@shared/ui/atoms/Colours';
import { BodyLarge, BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { KioskVisitor, KioskVisitorSignInState } from '../data/kiosk-service';

interface Props {
  visitor: KioskVisitor;
  onSignIn: (id: number) => void;
  onSignOut: (id: number) => void;
  disabled?: boolean;
}

export function VisitorRow({ visitor, onSignIn, onSignOut, disabled }: Props) {
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  const due = `${pad(visitor.timeDueHours)}:${pad(visitor.timeDueMinutes)}`;

  const renderActions = () => {
    if (visitor.signInState === KioskVisitorSignInState.NONE) {
      return (
        <ActionButton
          $variant="primary"
          onClick={() => onSignIn(visitor.id)}
          disabled={disabled}
        >Sign in</ActionButton>
      );
    }
    if (visitor.signInState === KioskVisitorSignInState.CHECKED_IN) {
      if (confirmSignOut) {
        return (
          <ConfirmGroup>
            <BodySmall>Sign out {visitor.visitorName}?</BodySmall>
            <ActionButton
              $variant="primary"
              onClick={() => { setConfirmSignOut(false); onSignOut(visitor.id); }}
              disabled={disabled}
            >Yes</ActionButton>
            <ActionButton
              $variant="ghost"
              onClick={() => setConfirmSignOut(false)}
              disabled={disabled}
            >Cancel</ActionButton>
          </ConfirmGroup>
        );
      }
      return (
        <ActionButton
          $variant="outline"
          onClick={() => setConfirmSignOut(true)}
          disabled={disabled}
        >Sign out</ActionButton>
      );
    }
    return <Badge>Signed out</Badge>;
  };

  return (
    <Row>
      <Info>
        <BodyLarge weight={600}>{visitor.visitorName || 'Unnamed visitor'}</BodyLarge>
        <BodyRegular style={{ color: Colours.darkGrey }}>
          {visitor.companyName ? visitor.companyName : '—'} · Due {due}
        </BodyRegular>
        {visitor.meetingRoomName && (
          <BodyRegular style={{ color: Colours.darkGrey }}>
            Meeting room: {visitor.meetingRoomName}
            {visitor.meetingStartDateTime && visitor.meetingEndDateTime && (
              <> · {formatTime(visitor.meetingStartDateTime)} to {formatTime(visitor.meetingEndDateTime)}</>
            )}
          </BodyRegular>
        )}
        {visitor.signInState === KioskVisitorSignInState.CHECKED_IN && visitor.checkedInTime && (
          <BodySmall style={{ color: Colours.darkGreen }}>
            Signed in {formatTime(visitor.checkedInTime)}
          </BodySmall>
        )}
        {visitor.signInState === KioskVisitorSignInState.CHECKED_OUT && visitor.checkedOutTime && (
          <BodySmall style={{ color: Colours.darkGrey }}>
            Signed out {formatTime(visitor.checkedOutTime)}
          </BodySmall>
        )}
      </Info>
      <Actions>{renderActions()}</Actions>
    </Row>
  );
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

function formatTime(ts: string): string {
  try {
    const d = new Date(ts);
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
}

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  background: white;
  border: 1px solid ${Colours.lightGrey};
  border-radius: 12px;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ConfirmGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Badge = styled.span`
  padding: 6px 12px;
  border-radius: 999px;
  background: ${Colours.iconGrey};
  color: ${Colours.darkGrey};
  font-size: 14px;
`;

const ActionButton = styled.button<{ $variant: 'primary' | 'outline' | 'ghost' }>`
  min-width: 96px;
  height: 56px;
  padding: 0 20px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${p => p.$variant === 'primary' ? Colours.blue : Colours.mildGrey};
  background: ${p => p.$variant === 'primary' ? Colours.blue : 'white'};
  color: ${p => p.$variant === 'primary' ? 'white' : (p.$variant === 'ghost' ? Colours.darkGrey : Colours.black)};
  &:disabled { opacity: 0.5; cursor: default; }
`;
